import { AxiosPromise } from "axios";
import {
  IDemandParameterModel,
  IDemandListing,
  IDemandModel,
  DemandModel,
  ISmartSearchParameter,
  IDemandSmartSuggestion,
  DemandPerson,
  IDemandProposalParameterModel
} from "../models/demand";
import { GenericBaseParameter } from "../models/base-parameter";
import { GenericServices } from "./generic.services";
import Uri from "../common/uri/demand-uri.constants";
import AutoCompleteUri from "../common/uri/autocomplete-uri.constants";
import { ApiResponse } from "../models/app-configuration";
import { Listing } from "../models/listing-base-model";
import { DataHelper } from "../utils/data-helper";
import { IDemandProposalListing } from "../models/demand/demand-proposal-listing.model";
import { MatchingDemandRequest, MatchingDemandResponse } from "../models/demand/demand-matching-parameter.model";

export class DemandServices extends GenericServices {
  /**
   *
   */
  constructor() {
    super();
  }
  initDemand(): Promise<IDemandModel> {
    return super
      .executeSelectingPost({}, Uri.changeInfoInit)
      .then((response: ApiResponse) => {
        if (response.isSuccess) {
          delete response.data.Id;
          response.data.PersonInfo = new DemandPerson();
          response.data.RelocationPersonInfo = new DemandPerson();
          return Promise.resolve(response.data);
        }
        return Promise.reject(response.message);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }
  getDemandById(id: number) {
    return super
      .executeSelectBy({ id }, Uri.detail)
      .then((response: ApiResponse) => {
        if (response.isSuccess) {
          return Promise.resolve(response.data);
        } else return Promise.reject(response.message);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }
  getListOfDemands(param: GenericBaseParameter<IDemandParameterModel>, cancelTokenSource?: any) {
    return super
      .executeSelectingPost(param, Uri.search, cancelTokenSource)
      .then((response: ApiResponse) => {
        if (response.isSuccess) {
          return Promise.resolve(
            new Listing<IDemandListing>("listOfDemands", response.data)
          );
        } else return Promise.reject(response.message);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }
  getListOfProposals(
    id: number,
    param: GenericBaseParameter<IDemandProposalParameterModel>
  ) {
    return super
      .executeSelecting(param, Uri.proposal.replace(":id", id.toString()))
      .then((response: ApiResponse) => {
        console.log("proposal", response.data);
        if (response.isSuccess) {
          return Promise.resolve(
            new Listing<IDemandProposalListing>(
              "listOfProposals",
              response.data
            )
          );
        } else return Promise.reject(response.message);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }
  getListOfCriterias(param: IDemandSmartSuggestion) {
    return super
      .executeSelectingPost(param, Uri.smartSuggestion)
      .then((response: ApiResponse) => {
        if (response.isSuccess) {
          return Promise.resolve(response.data);
        }
        return Promise.reject(response.message);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }
  quickSearchDemand<TResult>(query: string): AxiosPromise<TResult> {
    let path = AutoCompleteUri.demand + "/" + query;
    return this.executeSelecting<string, TResult>(null, path);
  }
  populateToken2Detail(entity: IDemandModel, categoryType?: string) {
    return super
      .executeCreatingWithParam<IDemandModel, any, any>(
        entity,
        { actionOn: categoryType || "map" },
        Uri.token2Detail
      )
      .then((response: ApiResponse) => {
        if (response.isSuccess) {
          return Promise.resolve(new DemandModel(response.data));
        } else return Promise.reject(response.message);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }
  populateDetail2Token(entity: IDemandModel) {
    return super
      .executeCreating<IDemandModel, any>(entity, Uri.detail2Token)
      .then((response: ApiResponse) => {
        if (response.isSuccess) {
          return Promise.resolve(response.data);
        } else return Promise.reject(response.message);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }
  populateCondition2Token(
    entity: GenericBaseParameter<ISmartSearchParameter>,
    categoryType?: string
  ) {
    return super
      .executeCreatingWithParam<
        GenericBaseParameter<ISmartSearchParameter>,
        any,
        any
      >(entity, { actionOn: categoryType || "map" }, Uri.condition2Token)
      .then((response: ApiResponse) => {
        if (response.isSuccess) return Promise.resolve(response.data);
        else return Promise.reject(response.message);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }
  populateToken2Condition(
    entity: GenericBaseParameter<ISmartSearchParameter>,
    categoryType?: string
  ) {
    return super
      .executeCreatingWithParam<
        GenericBaseParameter<ISmartSearchParameter>,
        any,
        any
      >(entity, { actionOn: categoryType || "map" }, Uri.token2Condition)
      .then((response: ApiResponse) => {
        if (response.isSuccess) return Promise.resolve(response.data);
        else return Promise.reject(response.message);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }
  insertDemand(demand: IDemandModel) {
    return super
      .executeCreating(demand, Uri.default)
      .then((response: ApiResponse) => {
        if (response.isSuccess) return Promise.resolve(response.data);
        else return Promise.reject(response);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }
  updateDemand(originDemand: IDemandModel, demand: IDemandModel) {
    const demandCloned = DataHelper.deepClone<IDemandModel>(demand);
    const originDemandCloned = DataHelper.deepClone<IDemandModel>(originDemand);

    delete demandCloned.InputEntries;
    delete demandCloned.RegionEntries;

    delete originDemandCloned.InputEntries;
    delete originDemandCloned.RegionEntries;

    return super.executeUpdating(originDemand.Id, originDemandCloned, demandCloned, Uri.default)
      .then((response: ApiResponse) => {
      return response.isSuccess
        ? Promise.resolve(response.data)
        : Promise.reject(response)
    })
      .catch(error => Promise.reject(error));
  }
  deleteDemand(ids: number[]) {
    if (ids.length <= 0)
      return Promise.reject("List of demand ids must be more than 0");
    return super
      .executeDeletingMulti(ids, Uri.default)
      .then((response: ApiResponse) => {
        return response.isSuccess
          ? Promise.resolve(response.data)
          : Promise.reject(response);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }
  calculateBudget(value: number) {
    return super
      .executeCreating({ Budget: value }, Uri.calculatePrice)
      .then((response: ApiResponse) => {
        return response.isSuccess
          ? Promise.resolve({
              PriceFrom: response.data.From,
              PriceTo: response.data.To
            })
          : Promise.reject(response.message);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }

  getQuickSearchDemands(ids: number[]) {
    if (ids.length <= 0)
      return Promise.reject("List of property ids must be more than 0");
    let url = Uri.getQuickSearchDemands.replace(":ids", ids.join(","));
    return super
      .executeSelectingNotEncode(undefined, url)
      .then((response: ApiResponse) => {
        return response.isSuccess
          ? Promise.resolve(response.data)
          : Promise.reject(response.message);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }

  getHtmlDescriptions(demandSources: any[]): Promise<any[]> {
    if (!demandSources || demandSources.length == 0) {
      return Promise.reject(false);
    }

    var ids = [];
    demandSources.forEach(d => {
      d.HtmlDescs = null;
      d.MatchingCount = 0;
      ids.push(d.Id);

    });

    return super.executeSelectingPost({ids}, Uri.getHtmlDescriptions).then((response: ApiResponse) => {
      if (!response.isSuccess) {
        return Promise.reject(response.message);
      }
      // new mapping-resources
      return Promise.resolve(demandSources.map((demand: any) => {
        demand.HtmlDescs = response.data.find(fd => fd.Id === demand.Id).HtmlDescs;
        return demand;
      }));
    }).catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
    });
  }

  getDemandPreviewById(id: string, tabs?: string) {
        return super
            .executeSelecting({
              tabs: tabs ? tabs : '*'
            }, Uri.getDemandPreviewById.replace(":id", id), false)
            .then((response: ApiResponse) => {
                if (response.isSuccess) {
                    return Promise.resolve(response.data);
                }
                return Promise.reject(response.message);
            })
            .catch(ex => {
                return Promise.reject(ex);
            });
    }

    getDemandConfigs()
    {
      return super
      .executeSelecting(undefined, Uri.getDemandConfigs)
      .then((response: ApiResponse) => {
          if (response.isSuccess) {
              return Promise.resolve(response.data);
          }
          return Promise.reject(response.message);
      })
      .catch(ex => {

          return Promise.reject(ex);
      });
    }

  matchingAllDemandIds(request: MatchingDemandRequest): Promise<number[]> {
      var cloneRequest = DataHelper.deepClone<MatchingDemandRequest>(request);
      cloneRequest.PageIndex = cloneRequest.PageSize = -1;
      cloneRequest.Fields = cloneRequest.OrderBy = "Id";
      cloneRequest.ViewMode = "Grid";
   
      return this.matchingDemands(cloneRequest)
          .then((response: MatchingDemandResponse) => Promise.resolve((response.Records || []).map(item => item["Id"])))
          .catch(error => Promise.reject(error));
  }

    matchingDemands(request: MatchingDemandRequest, forceFilter = null) {
    let url = Uri.matching.replace(':propertyId', request.PropertyId.toString());
    if(forceFilter !== null) { // Mapping at first time show demand list
      url = `${url}?forceFilter=${forceFilter}` // force backend filter true or false on check
    }
    return super.executeSelectingPost(request, url)
      .then((response: ApiResponse) => response.isSuccess ? Promise.resolve(new MatchingDemandResponse(response.data)) : Promise.reject(response.message))
      .catch(ex => Promise.reject(ex));
  }

  matchingCountDemands(propertyId: number): Promise<number> {
    if (!propertyId || propertyId <= 0) {
        return;
    }

    let url = Uri.matchingCount.replace(':propertyId', propertyId.toString());

    return super.executeSelecting(undefined, url)
      .then((response: ApiResponse) => response.isSuccess ? Promise.resolve(response.data.TotalRecords) : Promise.reject(response.message))
      .catch(ex => Promise.reject(ex));
    }
    
    deleteAll(payload) {
        return new Promise((resolve, reject) => {
            return super.executeDeletingWith(payload, Uri.deleteAll)
                .then((response: ApiResponse) => {
                    return response.isSuccess ? resolve(response.data) : reject(response.message);
                }).catch(error => {
                    return reject(error)
                })
        })
    }

    initDemandWithProperty(propertyId: number, personId?: number) {
        let url = `${Uri.initDemandWithProperty}?propertyId=${propertyId}`;
        if (personId) {
            url += `&personId=${personId}`
        }
        return super.executeSelecting(undefined, url)
            .then((response: ApiResponse) => {
                if (response.isSuccess) {
                    return Promise.resolve(response.data);
                }
                return Promise.reject(response.message);
            })
            .catch(ex => {
                return Promise.reject(ex);
            });
    }
}
